import { StakeWiseV3VaultAbi } from '../abis/stakewise'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/** Production subgraph for StakeWise V3 mainnet. Verified against
 *  the v3-sdk repo's documented endpoints (not the older The Graph
 *  hosted-service ID).
 *
 *  Replica fallback:
 *  `https://graphs-replica.stakewise.io/mainnet/subgraphs/name/stakewise/prod` */
const STAKEWISE_SUBGRAPH_URL =
  'https://graphs.stakewise.io/mainnet/subgraphs/name/stakewise/prod'

/** GraphQL query — pulls every non-claimed exit request for the user
 *  across all StakeWise V3 vaults. Returns per-request positionTicket,
 *  vault address, exit-queue index, and the assets earmarked for
 *  payout (totalAssets at request time). */
const EXIT_REQUESTS_QUERY = `
query UserExitRequests($user: Bytes!) {
  exitRequests(where: { owner: $user, isClaimed: false }) {
    id
    positionTicket
    vault { id }
    totalAssets
    exitedAssets
    exitQueueIndex
    isClaimable
    timestamp
    withdrawalTimestamp
  }
}
`

type SubgraphExitRequest = {
  id: string
  positionTicket: string
  vault: { id: string }
  totalAssets: string
  exitedAssets: string | null
  exitQueueIndex: string | null
  isClaimable: boolean
  timestamp: string
  withdrawalTimestamp: string | null
}

/**
 * StakeWise V3 osETH — multi-vault async exit queue, indexed only via
 * StakeWise's self-hosted subgraph.
 *
 * Sequence:
 *  1. GraphQL POST to the StakeWise subgraph for all non-claimed exit
 *     requests owned by `user`. Returns the list with per-vault
 *     positionTicket + (lagging) `isClaimable`.
 *  2. For each request that has an `exitQueueIndex` assigned, multicall
 *     `calculateExitedAssets(receiver, positionTicket, timestamp,
 *     exitQueueIndex)` on the vault to re-confirm finalization (the
 *     subgraph lags a few blocks; on-chain is the source of truth).
 *  3. Normalise to `LstWithdrawalRequest[]`.
 *
 * Subgraph downtime: returns `[]` and lets the orchestrator surface
 * "we don't know your requests" rather than fail-loud. The reader's
 * caller already tolerates per-reader empty results.
 *
 * Per-vault address surfaced in `lst` field overrides the registry's
 * osETH share-token address — StakeWise exits are per-vault, not
 * per-osETH (osETH is the protocol-wide minted share). Each request
 * carries its source vault so UIs can group / route claims correctly.
 */
export const readerStakeWise: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    let json: { data?: { exitRequests?: SubgraphExitRequest[] } }
    try {
      const res = await fetch(STAKEWISE_SUBGRAPH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: EXIT_REQUESTS_QUERY,
          variables: { user: user.toLowerCase() },
        }),
      })
      if (!res.ok) return []
      json = (await res.json()) as typeof json
    } catch {
      return []
    }
    const requests = json?.data?.exitRequests ?? []
    if (requests.length === 0) return []

    // On-chain re-check for requests with an assigned exit-queue index.
    // Requests without an index are unambiguously still pending — skip
    // the on-chain call for those.
    const onChainChecks = requests
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => r.exitQueueIndex !== null)
    const checkCalls = onChainChecks.map(({ r }) => ({
      address: r.vault.id,
      name: 'calculateExitedAssets',
      params: [
        user,
        BigInt(r.positionTicket),
        BigInt(r.timestamp),
        BigInt(r.exitQueueIndex!),
      ],
    }))
    let checkResults: unknown[] = []
    if (checkCalls.length > 0) {
      try {
        checkResults = (await multicallRetry({
          chain: chainId,
          calls: checkCalls as unknown as never[],
          abi: checkCalls.map(() => StakeWiseV3VaultAbi) as unknown as never,
        })) as unknown[]
      } catch {
        // On-chain re-check failed — fall back to subgraph claim flag.
        checkResults = []
      }
    }

    // Build a quick lookup: requestIdx → on-chain claimedAssets.
    const claimedByIdx = new Map<number, bigint>()
    for (let i = 0; i < onChainChecks.length; i++) {
      const cell = checkResults[i] as
        | { claimedAssets?: unknown }
        | unknown[]
        | undefined
      const claimed = Array.isArray(cell)
        ? toBigInt(cell[2])
        : toBigInt(cell?.claimedAssets)
      if (claimed !== undefined && claimed > 0n) {
        claimedByIdx.set(onChainChecks[i].idx, claimed)
      }
    }

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < requests.length; i++) {
      const r = requests[i]
      const amount = toBigInt(r.totalAssets) ?? 0n
      const onChainClaimed = claimedByIdx.get(i)
      const claimable = onChainClaimed !== undefined || r.isClaimable
      out.push({
        lst: r.vault.id.toLowerCase(),
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: r.positionTicket,
        amountUnderlying: (onChainClaimed ?? amount).toString(),
        status: claimable ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
